import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LogIn, LogOut, Coffee, CupSoda, CalendarOff, Clock } from "lucide-react";

const KIND_LABEL = { in: "Masuk", out: "Keluar", break_start: "Mulai Istirahat", break_end: "Selesai Istirahat", libur: "Libur" };

export default function Attendance() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState("");
  const ab = localStorage.getItem("branch_id") || "all";

  const load = () => { setLoading(true); api.get("/attendance").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {}); }, []);

  // Hanya tampilkan karyawan milik cabang yang sedang login/aktif
  const myStaff = useMemo(() => staff.filter((s) => (ab === "all" || !ab) ? true : s.branch_id === ab), [staff, ab]);

  const daily = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = `${r.staff_id}|${r.wib_date}`;
      if (!m[k]) m[k] = { staff_name: r.staff_name, date: r.wib_date, in: "", break_start: "", break_end: "", out: "", late: 0, ot: 0 };
      const d = m[k];
      if (r.kind === "in") d.in = r.wib_time;
      if (r.kind === "break_start") d.break_start = r.wib_time;
      if (r.kind === "break_end") d.break_end = r.wib_time;
      if (r.kind === "out") d.out = r.wib_time;
      d.late += r.late_minutes || 0; d.ot += r.overtime_minutes || 0;
    });
    return Object.values(m).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [rows]);

  const record = async (kind) => {
    if (!sel) return toast.error("Pilih karyawan");
    await api.post("/attendance", { staff_id: sel, kind });
    toast.success(`${KIND_LABEL[kind]} tercatat`); load();
  };

  const BTN = [
    { kind: "in", label: "Masuk", icon: LogIn, cls: "bg-green-600 hover:bg-green-700" },
    { kind: "out", label: "Keluar", icon: LogOut, cls: "bg-slate-600 hover:bg-slate-700" },
    { kind: "break_start", label: "Mulai Istirahat", icon: Coffee, cls: "bg-amber-500 hover:bg-amber-600" },
    { kind: "break_end", label: "Selesai Istirahat", icon: CupSoda, cls: "bg-amber-600 hover:bg-amber-700" },
    { kind: "libur", label: "Libur", icon: CalendarOff, cls: "bg-red-500 hover:bg-red-600" },
  ];

  return (
    <div>
      <PageHeader title="Absensi Staf" subtitle="Catat kehadiran karyawan (WIB)" />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-4">
        <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2 mb-4 w-fit" data-testid="shift-info">
          <Clock className="w-4 h-4" /> Jam kerja: <span className="font-semibold">09.30 – 22.00 WIB</span> (1 shift). Masuk &gt; 09.30 = terlambat · Pulang &gt; 22.00 = lembur.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="text-xs text-slate-500">Karyawan</Label>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="mt-1" data-testid="att-staff"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
              <SelectContent>{myStaff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {BTN.map((b) => {
            const Icon = b.icon;
            return (
              <Button key={b.kind} onClick={() => record(b.kind)} className={b.cls} data-testid={`att-${b.kind}`}>
                <Icon className="w-4 h-4 mr-1.5" /> {b.label}
              </Button>
            );
          })}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Nama Karyawan", "Tanggal", "Jam Masuk", "Mulai Istirahat", "Selesai Istirahat", "Jam Pulang", "Total Terlambat", "Total Lembur"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={8}><Loading /></td></tr>
              : daily.length === 0 ? <tr><td colSpan={8}><Empty /></td></tr>
              : daily.map((d, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{d.staff_name}</td>
                  <td className="px-4 py-3 text-slate-500">{d.date}</td>
                  <td className="px-4 py-3 font-mono-num text-green-600">{d.in || "-"}</td>
                  <td className="px-4 py-3 font-mono-num text-amber-600">{d.break_start || "-"}</td>
                  <td className="px-4 py-3 font-mono-num text-amber-600">{d.break_end || "-"}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{d.out || "-"}</td>
                  <td className="px-4 py-3 font-mono-num text-red-600">{d.late ? `${d.late}m` : "-"}</td>
                  <td className="px-4 py-3 font-mono-num text-green-600">{d.ot ? `${d.ot}m` : "-"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
